import { tr, uiLocale } from "../i18n/LanguageContext";
import React, { useState, useEffect } from 'react';
import { apiErrorMessage } from "../lib/apiError";
import { useLocation } from "react-router-dom";
import { useNavigate, localizeTo } from "../i18n/router";
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Users, Heart, Calendar, CheckCircle, ArrowRight, Clock, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function OrganizersApplyPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const formRef = React.useRef(null);
  const [formData, setFormData] = useState({
    displayName: user?.displayName || '',
    email: user?.email || '',
    bio: user?.bio || '',
    experience: '',
    motivation: ''
  });
  
  // Check if user already has an application
  useEffect(() => {
    const checkApplicationStatus = async () => {
      if (!user) {
        setCheckingStatus(false);
        return;
      }
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/organizers/application/status`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.hasApplication) {
            setExistingApplication(data);
          }
        }
      } catch (error) {
        console.error('Error checking application status:', error);
      } finally {
        setCheckingStatus(false);
      }
    };
    
    checkApplicationStatus();
  }, [user]);
  
  // Auto-scroll to form if hash is present (after login redirect)
  useEffect(() => {
    if (location.hash === '#apply-form' && formRef.current && !existingApplication) {
      setTimeout(() => {
        formRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 500);
    }
  }, [location.hash, existingApplication]);
  
  // Update form data when user logs in
  useEffect(() => {
    if (user) {
      const savedDraft = localStorage.getItem('organizerApplicationDraft');
      
      if (savedDraft) {
        try {
          const draftData = JSON.parse(savedDraft);
          setFormData(prev => ({
            ...draftData,
            displayName: user.displayName || draftData.displayName,
            email: user.email || draftData.email,
            bio: user.bio || draftData.bio
          }));
          localStorage.removeItem('organizerApplicationDraft');
        } catch (e) {
          console.error('Error loading draft:', e);
        }
      } else {
        setFormData(prev => ({
          ...prev,
          displayName: user.displayName || prev.displayName,
          email: user.email || prev.email,
          bio: user.bio || prev.bio
        }));
      }
    }
  }, [user]);
  
  const handleMainCTAClick = (e) => {
    if (user) {
      e.preventDefault();
      if (formRef.current) {
        formRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = {
      ...formData,
      [name]: value
    };
    setFormData(updatedData);
    localStorage.setItem('organizerApplicationDraft', JSON.stringify(updatedData));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login?redirect=/organizers/apply&after=showForm&intent=organizer_apply');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/organizers/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        // Clear draft on successful submission
        localStorage.removeItem('organizerApplicationDraft');
        setSubmitted(true);
      } else {
        const data = await response.json();
        toast({ title: apiErrorMessage(data, tr("Произошла ошибка при отправке заявки")), variant: "destructive" });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({ title: tr("Произошла ошибка при отправке заявки"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking application status
  if (checkingStatus && user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 rounded-full animate-spin" 
             style={{borderColor: 'var(--eunoia-accent-whisper)', borderTopColor: 'var(--eunoia-accent)'}} />
      </div>
    );
  }

  // Show existing application status
  if (user && existingApplication) {
    const statusLabels = {
      pending: tr("На рассмотрении"),
      approved: tr("Одобрена"),
      rejected: tr("Отклонена")
    };
    
    const statusColors = {
      pending: { bg: 'var(--eunoia-accent-whisper)', text: 'var(--eunoia-accent)' },
      approved: { bg: '#dcfce7', text: '#16a34a' },
      rejected: { bg: '#fee2e2', text: '#dc2626' }
    };
    
    const status = existingApplication.status || 'pending';
    const colors = statusColors[status] || statusColors.pending;
    
    return (
      <div className="min-h-screen eunoia-space-lg px-4">
        <div className="max-w-2xl mx-auto">
          <div className="eunoia-surface p-10 text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" 
                 style={{backgroundColor: colors.bg}}>
              {status === 'pending' && <Clock className="h-10 w-10" style={{color: colors.text}} />}
              {status === 'approved' && <CheckCircle className="h-10 w-10" style={{color: colors.text}} />}
              {status === 'rejected' && <FileText className="h-10 w-10" style={{color: colors.text}} />}
            </div>
            
            <h1 className="eunoia-title-large mb-4">
              {tr("Вы уже подали заявку")}
            </h1>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                 style={{backgroundColor: colors.bg}}>
              <span className="eunoia-body" style={{color: colors.text, fontWeight: 500}}>
                {tr("Статус:")} {statusLabels[status]}
              </span>
            </div>
            
            {existingApplication.createdAt && (
              <p className="eunoia-caption mb-6">
                {tr("Дата подачи:")} {new Date(existingApplication.createdAt).toLocaleDateString(uiLocale(), {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            )}
            
            {status === 'pending' && (
              <p className="eunoia-body mb-8 max-w-md mx-auto">
                {tr("Ваша заявка находится на рассмотрении. Мы свяжемся с вами по email после принятия решения.")}
              </p>
            )}
            
            {status === 'approved' && (
              <p className="eunoia-body mb-8 max-w-md mx-auto">
                {tr("Поздравляем! Ваша заявка одобрена. Теперь вы можете создавать события.")}
              </p>
            )}
            
            {status === 'rejected' && (
              <p className="eunoia-body mb-8 max-w-md mx-auto">
                {tr("К сожалению, ваша заявка была отклонена. Вы можете связаться с нами для уточнения причин.")}
              </p>
            )}
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {status === 'approved' && (
                <button
                  onClick={() => navigate('/add-event')}
                  className="eunoia-btn-primary"
                >
                  {tr("Создать событие")}
                </button>
              )}
              <button
                onClick={() => navigate('/profile')}
                className="eunoia-btn-secondary"
              >
                {tr("В профиль")}
              </button>
              <button
                onClick={() => navigate('/')}
                className="eunoia-btn-secondary"
              >
                {tr("На главную")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show success message after submission
  if (submitted) {
    return (
      <div className="min-h-screen eunoia-space-lg px-4">
        <div className="max-w-2xl mx-auto">
          <div className="eunoia-surface p-10 text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" 
                 style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}>
              <CheckCircle className="h-10 w-10" style={{color: 'var(--eunoia-accent)'}} />
            </div>
            
            <h1 className="eunoia-title-large mb-4">
              {tr("Заявка отправлена!")}
            </h1>
            
            <div className="eunoia-surface-soft p-6 rounded-xl mb-8 text-left max-w-md mx-auto">
              <p className="eunoia-body mb-4">
                <strong>{tr("Ваша заявка отправлена и находится на рассмотрении.")}</strong>
              </p>
              <p className="eunoia-body">
                {tr("Мы свяжемся с вами по email в ближайшее время.")}
              </p>
            </div>
            
            <p className="eunoia-caption mb-8">
              {tr("Вы можете проверить статус заявки в вашем профиле")}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/profile')}
                className="eunoia-btn-primary"
              >
                {tr("Перейти в профиль")}
              </button>
              <button
                onClick={() => navigate('/')}
                className="eunoia-btn-secondary"
              >
                {tr("На главную")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen eunoia-space-lg px-4 pb-16">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Users className="h-8 w-8" style={{color: 'var(--eunoia-accent)'}} />
            <h1 className="eunoia-title-large">
              {tr("Стать организатором")}
            </h1>
          </div>
          <p className="eunoia-body-large max-w-2xl mx-auto">
            {tr("Помогите создавать пространство для духовного роста и практики.")}
            {tr("Организаторы EUNOIA вдохновляют и объединяют сообщество через события.")}
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="eunoia-surface-soft p-6 text-center">
            <Calendar className="h-8 w-8 mx-auto mb-4" style={{color: 'var(--eunoia-accent)'}} />
            <h3 className="eunoia-title-small mb-2">{tr("Создавайте события")}</h3>
            <p className="eunoia-body">
              {tr("Делитесь практиками и опытом с сообществом")}
            </p>
          </div>
          
          <div className="eunoia-surface-soft p-6 text-center">
            <Users className="h-8 w-8 mx-auto mb-4" style={{color: 'var(--eunoia-accent)'}} />
            <h3 className="eunoia-title-small mb-2">{tr("Объединяйте людей")}</h3>
            <p className="eunoia-body">
              {tr("Создавайте пространство для встреч единомышленников")}
            </p>
          </div>
          
          <div className="eunoia-surface-soft p-6 text-center">
            <Heart className="h-8 w-8 mx-auto mb-4" style={{color: 'var(--eunoia-accent)'}} />
            <h3 className="eunoia-title-small mb-2">{tr("Развивайте сообщество")}</h3>
            <p className="eunoia-body">
              {tr("Способствуйте росту и трансформации")}
            </p>
          </div>
        </div>

        {/* Main CTA */}
        <div className="text-center mb-12">
          <a
            href={user ? "#apply-form" : "/login?redirect=/organizers/apply&after=showForm&intent=organizer_apply"}
            onClick={handleMainCTAClick}
            className="eunoia-btn-primary text-lg px-12 py-4 inline-flex items-center gap-3"
            style={{textDecoration: 'none'}}
          >
            <Users className="h-5 w-5" />
            {tr("Стать организатором")}
            <ArrowRight className="h-5 w-5" />
          </a>
          <p className="eunoia-caption mt-4">
            {user ? tr("Заполните заявку ниже") : tr("Заполните заявку ниже и войдите для отправки")}
          </p>
        </div>

        {/* Application Form */}
        <div id="apply-form" ref={formRef} className="eunoia-surface p-8">
          <h2 className="eunoia-title-medium mb-6">{tr("Заявка на роль организатора")}</h2>
          
          {!user && (
            <div className="eunoia-surface-soft p-4 mb-6 rounded-xl" style={{borderLeft: '3px solid var(--eunoia-accent)'}}>
              <p className="eunoia-body text-sm">
                {tr("Заполните форму ниже. При нажатии \"Подать заявку\" вы сможете войти и отправить заявку.")}
              </p>
            </div>
          )}
            
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="displayName" className="block eunoia-body mb-2">
                {tr("Ваше имя *")}
              </label>
              <input
                id="displayName"
                name="displayName"
                type="text"
                value={formData.displayName}
                onChange={handleChange}
                className="eunoia-input"
                required
                disabled={loading}
                placeholder={tr("Как вас зовут?")}
              />
            </div>

            <div>
              <label htmlFor="email" className="block eunoia-body mb-2">
                Email *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="eunoia-input"
                required
                disabled={loading}
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label htmlFor="bio" className="block eunoia-body mb-2">
                {tr("О вас *")}
              </label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                className="eunoia-input min-h-[100px] resize-y"
                placeholder={tr("Расскажите о себе, вашем пути и практике...")}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="experience" className="block eunoia-body mb-2">
                {tr("Опыт организации событий *")}
              </label>
              <textarea
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                className="eunoia-input min-h-[120px] resize-y"
                placeholder={tr("Опишите ваш опыт организации духовных практик, ретритов или других событий...")}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="motivation" className="block eunoia-body mb-2">
                {tr("Мотивация *")}
              </label>
              <textarea
                id="motivation"
                name="motivation"
                value={formData.motivation}
                onChange={handleChange}
                className="eunoia-input min-h-[120px] resize-y"
                placeholder={tr("Почему вы хотите стать организатором в EUNOIA? Какие события вы хотели бы создавать?")}
                required
                disabled={loading}
              />
            </div>

            <div className="pt-4">
              {user ? (
                <button
                  type="submit"
                  className="eunoia-btn-primary w-full flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {tr("Отправить заявку")}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              ) : (
                <a
                  href={localizeTo("/login?redirect=/organizers/apply&after=showForm&intent=organizer_apply")}
                  className="eunoia-btn-primary w-full flex items-center justify-center gap-2"
                  style={{textDecoration: 'none'}}
                >
                  {tr("Войти и отправить заявку")}
                  <ArrowRight className="h-4 w-4" />
                </a>
              )}
            </div>
          </form>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/organizers')}
            className="eunoia-caption hover:underline"
            style={{color: 'var(--eunoia-accent)'}}
          >
            {tr("← Вернуться к списку организаторов")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrganizersApplyPage;
